import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Tag, User, Share2, Facebook, Linkedin, Copy, Check, Loader2 } from 'lucide-react';

// Custom X (Twitter) icon component
const XTwitterIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  featured_image: string | null;
  thumbnail_image: string | null;
  category: string | null;
  tags: string[] | null;
  reading_time: number;
  published_at: string | null;
  author_name: string;
  meta_title: string | null;
  meta_description: string | null;
  view_count: number;
}

const API_URL = import.meta.env.VITE_API_URL || 'https://sweetpilot.com';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchPost();
    }
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/blog/posts/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('Post not found');
        } else {
          throw new Error('Failed to fetch post');
        }
        return;
      }
      const data = await response.json() as { success: boolean; data?: BlogPost };
      if (data.success && data.data) {
        setPost(data.data);
      } else {
        setError('Post not found');
      }
    } catch (err) {
      setError('Failed to load blog post');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getTags = (tags: string[] | null): string[] => {
    if (!tags) return [];
    return tags;
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || '';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareOnTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
      '_blank'
    );
  };

  const shareOnFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      '_blank'
    );
  };

  const shareOnLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      '_blank'
    );
  };

  // Loading state
  if (loading) {
    return (
      <StaticPageLayout title="Loading..." subtitle="">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </StaticPageLayout>
    );
  }

  // Error state
  if (error || !post) {
    return (
      <StaticPageLayout title="Post Not Found" subtitle="">
        <div className={`text-center py-20 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <p className="text-6xl mb-4">404</p>
          <p className="text-xl font-semibold mb-2">{error || 'Post not found'}</p>
          <p className="mb-6">The blog post you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </StaticPageLayout>
    );
  }

  return (
    <StaticPageLayout
      title=""
      subtitle=""
    >
      {/* Back to blog link */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <Link
          to="/blog"
          className={`inline-flex items-center gap-2 transition-colors ${
            isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>
      </motion.div>

      {/* Article Header */}
      <motion.article
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto"
      >
        {/* Category and Meta */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {post.category && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 text-sm">
              <Tag className="w-3 h-3" />
              {post.category}
            </span>
          )}
          <span className={`flex items-center gap-1 text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
            <Calendar className="w-4 h-4" />
            {formatDate(post.published_at)}
          </span>
          <span className={`flex items-center gap-1 text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
            <Clock className="w-4 h-4" />
            {post.reading_time} min read
          </span>
        </div>

        {/* Title */}
        <h1 className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight ${
          isDark ? 'text-white' : 'text-slate-900'
        }`}>
          {post.title}
        </h1>

        {/* Excerpt */}
        {post.excerpt && (
          <p className={`text-xl mb-8 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
            {post.excerpt}
          </p>
        )}

        {/* Author */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {post.author_name}
            </div>
            <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
              Author
            </div>
          </div>
        </div>

        {/* Featured Image */}
        {post.featured_image && (
          <div className="mb-10 rounded-2xl overflow-hidden">
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-auto object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div
          className={`prose prose-lg max-w-none mb-12 ${
            isDark
              ? '[&_p]:text-gray-300 [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_strong]:text-white [&_a]:text-purple-400 [&_li]:text-gray-300 [&_ul]:text-gray-300'
              : '[&_p]:text-slate-700 [&_h1]:text-slate-900 [&_h2]:text-slate-900 [&_h3]:text-slate-800 [&_strong]:text-slate-900 [&_a]:text-purple-600 [&_li]:text-slate-700 [&_ul]:text-slate-700'
          }`}
          dangerouslySetInnerHTML={{ __html: formatContent(post.content) }}
        />

        {/* Tags */}
        {post.tags && getTags(post.tags).length > 0 && (
          <div className="mb-8">
            <h3 className={`text-sm font-semibold mb-3 uppercase tracking-wider ${
              isDark ? 'text-gray-500' : 'text-slate-500'
            }`}>
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {getTags(post.tags).map((tag, index) => (
                <span
                  key={index}
                  className={`px-3 py-1 rounded-full text-sm ${
                    isDark
                      ? 'bg-dark-700 text-gray-400'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Share */}
        <div className={`border-t pt-8 ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
          <h3 className={`text-sm font-semibold mb-4 uppercase tracking-wider flex items-center gap-2 ${
            isDark ? 'text-gray-500' : 'text-slate-500'
          }`}>
            <Share2 className="w-4 h-4" />
            Share this article
          </h3>
          <div className="flex items-center gap-3">
            <button
              onClick={shareOnTwitter}
              className={`p-3 rounded-xl transition-colors ${
                isDark
                  ? 'bg-dark-700 hover:bg-dark-600 text-gray-400 hover:text-white'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900'
              }`}
              title="Share on X"
            >
              <XTwitterIcon className="w-5 h-5" />
            </button>
            <button
              onClick={shareOnFacebook}
              className={`p-3 rounded-xl transition-colors ${
                isDark
                  ? 'bg-dark-700 hover:bg-dark-600 text-gray-400 hover:text-white'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900'
              }`}
              title="Share on Facebook"
            >
              <Facebook className="w-5 h-5" />
            </button>
            <button
              onClick={shareOnLinkedIn}
              className={`p-3 rounded-xl transition-colors ${
                isDark
                  ? 'bg-dark-700 hover:bg-dark-600 text-gray-400 hover:text-white'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900'
              }`}
              title="Share on LinkedIn"
            >
              <Linkedin className="w-5 h-5" />
            </button>
            <button
              onClick={handleCopyLink}
              className={`p-3 rounded-xl transition-colors ${
                isDark
                  ? 'bg-dark-700 hover:bg-dark-600 text-gray-400 hover:text-white'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900'
              }`}
              title="Copy link"
            >
              {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Newsletter CTA */}
        <div className={`mt-12 text-center p-8 rounded-2xl border ${
          isDark
            ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
            : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-slate-200'
        }`}>
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Enjoyed this article?
          </h2>
          <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
            Subscribe to get the latest insights delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className={`flex-1 px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-purple-500/50 ${
                isDark
                  ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500'
                  : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
              }`}
            />
            <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white hover:opacity-90 transition-opacity">
              Subscribe
            </button>
          </div>
        </div>
      </motion.article>
    </StaticPageLayout>
  );
}

// Convert markdown-style content to HTML
function formatContent(content: string): string {
  // Basic markdown to HTML conversion
  let html = content
    // Headers
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    // Line breaks
    .replace(/\n\n/g, '</p><p>')
    // Lists
    .replace(/^\- (.*$)/gm, '<li>$1</li>');

  // Wrap in paragraphs
  html = '<p>' + html + '</p>';

  // Fix list items
  html = html.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');

  return html;
}
